import { Accordion, Button, MenuTrigger, Stack, Text } from '@eleveo/components-react';
import { FilterClearIcon, ThreeDotsIcon, UndoIcon } from '@eleveo/icons-react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { toZoned, today } from '@internationalized/date';
import { equals } from 'ramda';
import React, { useState } from 'react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { useDateFormatter } from 'react-aria';
import { Trans, useTranslation } from 'react-i18next';

import {
  ACTIVE_SIDEBAR_FILTERS_COUNTER,
  APPLY_FILTER_BUTTON_TAG,
  FILTER_SIDEBAR_DROPDOWN_TOGGLE,
  RESET_ALL_SIDEBAR_FILTERS_BUTTON_TAG,
  TO_DEFAULT_ALL_SIDEBAR_FILTERS_BUTTON_TAG,
} from './constants';
import { useAuth } from '../../app/AuthorizationProvider';
import { Sidebar, SidebarContent, SidebarPanel, SidebarPart } from '../../components/LayoutNext/LayoutNext';
import {
  CheckboxFilterTile,
  getCheckboxFilterTileSubtitle,
  stateItems,
  subtypeItems,
} from '../../components/LayoutNext/components/CheckboxFilterTile';
import { DateRangeFilterTile } from '../../components/LayoutNext/components/DateRangeFilterTile';
import { isDateRangeFilterAccordionType } from '../../components/LayoutNext/components/filterTileUtils';
import { QueuesResult } from '../../graphql/types';
import { DateRangeFilter } from '../../utils/filter';

import type { CheckboxFilterTileProps } from '../../components/LayoutNext/components/CheckboxFilterTile';
import type { DateRangeFilterTileProps } from '../../components/LayoutNext/components/DateRangeFilterTile';

export const getDefaultRequestsSidebarFilter = (timezone: string) =>
  ({
    createdRange: {
      start: toZoned(today(timezone).subtract({ days: 30 }), timezone),
      end: toZoned(today(timezone), timezone),
    },
    requestedRange: null as unknown as DateRangeFilter,
    queues: [],
    states: [],
    subtypes: [],
  } as RequestsSidebarFilterState);

export type RequestsSidebarFilterState = {
  createdRange: DateRangeFilter;
  requestedRange: DateRangeFilter;
  queues: string[];
  states: string[];
  subtypes: string[];
};

export type RequestsFilterSidebarProps = {
  handleCancel: () => void;
  preOpenedItemIds: string[];
  setPreOpenedItemIds: React.Dispatch<React.SetStateAction<string[]>>;
  setSidebarFilter: (filter: RequestsSidebarFilterState) => void;
  sidebarFilter: RequestsSidebarFilterState;
  queuesResult: QueuesResult;
};

export const RequestsFilterSidebar: React.FC<RequestsFilterSidebarProps> = ({
  handleCancel,
  preOpenedItemIds,
  setPreOpenedItemIds,
  setSidebarFilter,
  sidebarFilter,
  queuesResult,
}) => {
  const { t } = useTranslation();

  const { timezone } = useAuth();

  const formatter = useDateFormatter({ dateStyle: 'short', timeZone: timezone });

  const subtypes = subtypeItems(t);
  const states = stateItems(t);
  const queues = queuesResult.data?.queues ?? [];

  const defaultSelectedSubtypeIds = sidebarFilter.subtypes;
  const defaultSelectedStateIds = sidebarFilter.states;
  const defaultSelectedQueueIds = sidebarFilter.queues;

  // Filters states
  const [selectedSubtypeIds, setSelectedSubtypeIds] = useState(defaultSelectedSubtypeIds);
  const [selectedStateIds, setSelectedStateIds] = useState(defaultSelectedStateIds);
  const [selectedQueueIds, setSelectedQueueIds] = useState(defaultSelectedQueueIds);

  const [selectedCreatedRange, setSelectedCreatedRange] = useState<DateRangeFilter>(sidebarFilter.createdRange);

  const [selectedRequestedRange, setSelectedRequestedRange] = useState<DateRangeFilter>(sidebarFilter.requestedRange);

  const handleApply = () => {
    setSidebarFilter({
      subtypes: selectedSubtypeIds,
      queues: selectedQueueIds,
      states: selectedStateIds,
      createdRange: selectedCreatedRange,
      requestedRange: selectedRequestedRange,
    });

    handleCancel();
  };

  const handleResetAllFilters = () => {
    setSelectedSubtypeIds([]);
    setSelectedStateIds([]);
    setSelectedQueueIds([]);
    setSelectedCreatedRange(null as unknown as DateRangeFilter);
    setSelectedRequestedRange(null as unknown as DateRangeFilter);

    setPreOpenedItemIds([]);
  };

  const handleResetToDefaultAllFilters = () => {
    setSelectedSubtypeIds([]);
    setSelectedStateIds([]);
    setSelectedQueueIds([]);
    setSelectedCreatedRange(getDefaultRequestsSidebarFilter(timezone).createdRange);
    setSelectedRequestedRange(getDefaultRequestsSidebarFilter(timezone).requestedRange);

    setPreOpenedItemIds([]);
  };

  const isCreatedRangeFilterActive = sidebarFilter.createdRange?.start && sidebarFilter.createdRange?.end;
  const isRequestedRangeFilterActive = sidebarFilter.requestedRange?.start && sidebarFilter.requestedRange?.end;

  const countOfActiveFilters = [
    !!defaultSelectedSubtypeIds.length,
    !!defaultSelectedStateIds.length,
    !!defaultSelectedQueueIds.length,
    !!isCreatedRangeFilterActive,
    !!isRequestedRangeFilterActive,
  ].filter((active) => active).length;

  const isFilterActive =
    selectedSubtypeIds.length > 0 ||
    selectedStateIds.length > 0 ||
    selectedQueueIds.length > 0 ||
    isCreatedRangeFilterActive ||
    isRequestedRangeFilterActive;

  const isApplyActive =
    equals(selectedSubtypeIds, defaultSelectedSubtypeIds) &&
    equals(selectedStateIds, defaultSelectedStateIds) &&
    equals(selectedQueueIds, defaultSelectedQueueIds) &&
    equals(sidebarFilter.createdRange?.start, selectedCreatedRange?.start) &&
    equals(sidebarFilter.createdRange?.end, selectedCreatedRange?.end) &&
    equals(sidebarFilter.requestedRange?.start, selectedRequestedRange?.start) &&
    equals(sidebarFilter.requestedRange?.end, selectedRequestedRange?.end);

  // Accordion filter options
  const filters: [DateRangeFilterTileProps, DateRangeFilterTileProps, ...CheckboxFilterTileProps[]] = [
    {
      testId: 'requests-sidebar-filter-created-range',
      id: '1',
      subtitle:
        selectedCreatedRange?.start && selectedCreatedRange?.end
          ? `${formatter.format(selectedCreatedRange?.start.toDate(timezone))} - ${formatter.format(
              selectedCreatedRange?.end.toDate(timezone)
            )}`
          : '',
      subtitleColor: 'info',
      title: t('schedule.request.createdRange'),
      values: selectedCreatedRange,
      setValues: setSelectedCreatedRange,
    },
    {
      testId: 'requests-sidebar-filter-requested-range',
      id: '2',
      subtitle:
        selectedRequestedRange?.start && selectedRequestedRange?.end
          ? `${formatter.format(selectedRequestedRange?.start.toDate(timezone))} - ${formatter.format(
              selectedRequestedRange?.end.toDate(timezone)
            )}`
          : '',
      subtitleColor: 'info',
      title: t('schedule.request.requestedRange'),
      values: selectedRequestedRange,
      setValues: setSelectedRequestedRange,
    },
    {
      testId: 'requests-sidebar-filter-subtypes',
      id: '3',
      items: subtypes,
      onSelectionChange: setSelectedSubtypeIds,
      selectedKeys: selectedSubtypeIds,
      subtitle: getCheckboxFilterTileSubtitle(t, subtypes, selectedSubtypeIds),
      subtitleColor: 'info',
      title: t('schedule.request.requestType.requestType'),
    },
    {
      testId: 'requests-sidebar-filter-states',
      id: '4',
      items: states,
      onSelectionChange: setSelectedStateIds,
      selectedKeys: selectedStateIds,
      sortByField: 'textValue',
      subtitle: getCheckboxFilterTileSubtitle(t, states, selectedStateIds),
      subtitleColor: 'info',
      title: t('dataImport.status.status'),
    },
    {
      testId: 'requests-sidebar-filter-queues',
      id: '5',
      items: queues,
      onSelectionChange: setSelectedQueueIds,
      selectedKeys: selectedQueueIds,
      subtitle: getCheckboxFilterTileSubtitle(t, queues, selectedQueueIds),
      subtitleColor: 'info',
      title: t('queue.queues'),
    },
  ];

  return (
    <Sidebar onClose={handleCancel} title={t('filter.filters')}>
      <SidebarPanel>
        <Stack direction="horizontal" className="w-full items-center justify-between">
          <Button testId={APPLY_FILTER_BUTTON_TAG} isDisabled={isApplyActive} color="success" onClick={handleApply}>
            {t('form.apply')}
          </Button>
          <Text
            color={!countOfActiveFilters ? { light: 'grey-500', dark: 'grey-300' } : {}}
            testId={ACTIVE_SIDEBAR_FILTERS_COUNTER}
          >
            <Trans i18nKey="filter.activeFilters" values={{ count: countOfActiveFilters }}>
              {/* space needs to be added within strong tag so the tests can run correctly */}
              {/* @ts-expect-error: Value needs to be interpolated, using double bracelets */}
              <strong>{{ countOfActiveFilters }} </strong>Active filters
            </Trans>
          </Text>

          <MenuTrigger
            items={[
              {
                id: '1',
                text: t('filter.resetToDefault'),
                onClick: handleResetToDefaultAllFilters,
                icon: <UndoIcon />,
                testId: TO_DEFAULT_ALL_SIDEBAR_FILTERS_BUTTON_TAG,
              },
              {
                id: '2',
                text: t('filter.resetAllFilters'),
                onClick: handleResetAllFilters,
                icon: <FilterClearIcon />,
                isDisabled: !isFilterActive,
                testId: RESET_ALL_SIDEBAR_FILTERS_BUTTON_TAG,
              },
            ]}
          >
            <Button aria-label={t('filter.options')} variant="outline" testId={FILTER_SIDEBAR_DROPDOWN_TOGGLE}>
              <ThreeDotsIcon />
            </Button>
          </MenuTrigger>
        </Stack>
      </SidebarPanel>

      <SidebarContent>
        <SidebarPart>
          <Accordion
            expandedKeys={preOpenedItemIds}
            items={filters}
            expansionMode="single"
            onExpandedChange={(keys) => setPreOpenedItemIds([...keys] as string[])}
          >
            {(item) => {
              // Calendar Range filter type/content
              if (isDateRangeFilterAccordionType(item)) {
                return <DateRangeFilterTile {...item} />;
              }
              return <CheckboxFilterTile {...item} />;
            }}
          </Accordion>
        </SidebarPart>
      </SidebarContent>
    </Sidebar>
  );
};
